import React, { useMemo, useState } from 'react';
import { Button, Card, Empty, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { getExternalApplicationDisplayLines } from '../../utils/applicationDisplayFormat';
import TaskCard from '../tasks/TaskCard';
import { StatusCell } from '../../ui';
import type { CompletionProjection, WorkspaceApplication, WorkspaceTask } from '../../store/api/workspaceApi';
import type { ScopedTaskPresentationContext } from '../../domain/scopedTaskPresentation';
import { WORKSPACE_APPLICATION_GRID_ROW, WORKSPACE_GRID_CELL, WORKSPACE_ROUTE_GRID_ROW } from './workspaceGrid';
import {
  WORKSPACE_APPLICATION_HEADER_ROW,
  WORKSPACE_ROUTE_HEADER_ROW,
  WorkspaceHeaderCell,
  WorkspaceListToolbar,
  workspaceHighlightForApplication,
  workspaceHighlightForRoute,
  workspaceRowHighlightStyle,
  matchesApplicationStatusFilter,
  matchesRouteStatusFilter,
  toggleSort,
  type WorkspaceHasTasksFilter,
  type WorkspaceSortKey,
  type WorkspaceStatusFilter,
} from './workspaceListShared';

import {
  WORKSPACE_TASK_CARD_BODY_PADDING_PX,
  WORKSPACE_TASK_GROUP_EXPANDED_PADDING,
  WORKSPACE_TASK_GROUP_MARGIN_BOTTOM_PX,
  WORKSPACE_TASK_LIST_SCROLL_STYLE,
  WORKSPACE_SECTION_HEIGHT,
} from './workspaceDensity';

/** Шапка колонок: заявки с задачами — та же сетка, что «Все заявки». */
const WORKSPACE_APPLICATION_TASKS_STICKY_HEADER: React.CSSProperties = {
  ...WORKSPACE_APPLICATION_HEADER_ROW,
  position: 'sticky',
  top: 0,
  zIndex: 2,
  background: '#ffffff',
};

/** Шапка таблицы маршрутов с задачами (7 колонок). */
const WORKSPACE_ROUTE_TASK_STICKY_HEADER: React.CSSProperties = {
  ...WORKSPACE_ROUTE_HEADER_ROW,
  zIndex: 2,
  background: '#ffffff',
};

const { Text } = Typography;
export { WORKSPACE_SECTION_HEIGHT };

const WORKSPACE_TASK_CARD_BODY_PADDING: React.CSSProperties = {
  padding: WORKSPACE_TASK_CARD_BODY_PADDING_PX,
};

export { WORKSPACE_GRID_CELL, WORKSPACE_GRID_ROW, WORKSPACE_ROUTE_GRID_ROW } from './workspaceGrid';

export function formatWorkspaceTasksCount(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return `${n} задач`;
  if (mod10 === 1) return `${n} задача`;
  if (mod10 >= 2 && mod10 <= 4) return `${n} задачи`;
  return `${n} задач`;
}

export function formatWorkspaceDayMonth(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD.MM') : '—';
}

/** Две строки для колонки «№ заявки»: номер (№…) и дата ДД.ММ.ГГГГ. */
export function workspaceApplicationNumberLines(
  internalNumber: string | null | undefined,
  dateIso: string | null | undefined
): { line1: string; line2: string } {
  const num = String(internalNumber || '').trim();
  const line1 = num ? `№${num}` : '—';
  if (!dateIso) return { line1, line2: '—' };
  const d = dayjs(dateIso);
  return { line1, line2: d.isValid() ? d.format('DD.MM.YYYY') : '—' };
}

/** Две строки для колонки «Заявка клиента»: внешний № и дата (basisDate). */
export function workspaceClientApplicationLines(
  externalNumber: string | null | undefined,
  externalDateIso: string | null | undefined
): { line1: string; line2: string } {
  return getExternalApplicationDisplayLines({ externalNumber, basisDate: externalDateIso });
}

export function WorkspaceTwoLineTableCell({ line1, line2 }: { line1: string; line2: string }) {
  return (
    <div style={{ minWidth: 0, lineHeight: 1.3 }} title={`${line1}\n${line2}`}>
      <div>{line1}</div>
      <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
        {line2}
      </Text>
    </div>
  );
}

export function formatWorkspaceCarrierAgreementLine(
  agreement?: { number?: string | null; date?: string | null } | null
): string {
  if (!agreement) return '—';
  const num = agreement.number?.trim();
  const datePart = agreement.date ? formatWorkspaceDayMonth(agreement.date) : null;
  if (num && datePart && datePart !== '—') return '\u2116 ' + num + ' от ' + datePart;
  if (num) return '\u2116 ' + num;
  if (datePart && datePart !== '—') return `от ${datePart}`;
  return '—';
}

type RouteTaskGroup = {
  key: string;
  routeId: number;
  routeStatus: string | null;
  loadingCity: string | null;
  unloadingCity: string | null;
  carrierName: string | null;
  carrierAgreement: { number?: string | null; date?: string | null } | null;
  tasks: WorkspaceTask[];
  hasOverdue: boolean;
  nearestDueDate: string | null;
};

type ApplicationTaskGroup = {
  key: string;
  applicationId: number;
  applicationStatus: string | null;
  applicationNumber: string | null;
  applicationExternalNumber: string | null;
  applicationDate: string | null;
  applicationCreatedAt: string | null;
  applicationDirection: string | null;
  clientName: string | null;
  tasks: WorkspaceTask[];
  hasOverdue: boolean;
  nearestDueDate: string | null;
};

function sortByDue(a: WorkspaceTask, b: WorkspaceTask): number {
  const ta = a.dueDate ? dayjs(a.dueDate).valueOf() : Number.POSITIVE_INFINITY;
  const tb = b.dueDate ? dayjs(b.dueDate).valueOf() : Number.POSITIVE_INFINITY;
  return ta - tb;
}

function isOverdueTask(task: WorkspaceTask): boolean {
  return String(task.status || '').toUpperCase() === 'OVERDUE';
}

function dedupeTasks(tasks: WorkspaceTask[]): WorkspaceTask[] {
  const map = new Map<number, WorkspaceTask>();
  tasks.forEach((task) => {
    map.set(task.id, task);
  });
  return Array.from(map.values());
}

function groupTasksByRoute(tasks: WorkspaceTask[]): RouteTaskGroup[] {
  const grouped = new Map<string, RouteTaskGroup>();

  tasks
    .slice()
    .sort((a, b) => {
      const overdueDiff = Number(isOverdueTask(b)) - Number(isOverdueTask(a));
      if (overdueDiff !== 0) return overdueDiff;
      const dueDiff = sortByDue(a, b);
      if (dueDiff !== 0) return dueDiff;
      return a.id - b.id;
    })
    .forEach((task) => {
      const routeId = task.routeId;
      if (routeId == null) return;
      const key = `route-${routeId}`;

      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          routeId,
          routeStatus: task.routeStatus ?? null,
          loadingCity: task.loadingCity ?? null,
          unloadingCity: task.unloadingCity ?? null,
          carrierName: task.carrierName ?? null,
          carrierAgreement: task.carrierAgreement ?? null,
          tasks: [],
          hasOverdue: false,
          nearestDueDate: null
        });
      }

      const group = grouped.get(key)!;
      group.tasks.push(task);
      if (
        task.carrierAgreement &&
        (task.carrierAgreement.number?.trim() || task.carrierAgreement.date) &&
        !group.carrierAgreement
      ) {
        group.carrierAgreement = task.carrierAgreement;
      }
      group.hasOverdue = group.hasOverdue || isOverdueTask(task);

      const taskDue = task.dueDate ? dayjs(task.dueDate) : null;
      const currentDue = group.nearestDueDate ? dayjs(group.nearestDueDate) : null;
      if (taskDue?.isValid() && (!currentDue || !currentDue.isValid() || taskDue.isBefore(currentDue))) {
        group.nearestDueDate = task.dueDate ?? null;
      }
    });

  return Array.from(grouped.values()).sort((a, b) => {
    const aDue = a.nearestDueDate ? dayjs(a.nearestDueDate).valueOf() : Number.POSITIVE_INFINITY;
    const bDue = b.nearestDueDate ? dayjs(b.nearestDueDate).valueOf() : Number.POSITIVE_INFINITY;
    if (aDue !== bDue) return aDue - bDue;
    if (a.routeId !== b.routeId) return a.routeId - b.routeId;
    return a.key.localeCompare(b.key);
  });
}

function groupTasksByApplication(tasks: WorkspaceTask[]): ApplicationTaskGroup[] {
  const grouped = new Map<string, ApplicationTaskGroup>();

  tasks
    .slice()
    .sort((a, b) => {
      const overdueDiff = Number(isOverdueTask(b)) - Number(isOverdueTask(a));
      if (overdueDiff !== 0) return overdueDiff;
      const dueDiff = sortByDue(a, b);
      if (dueDiff !== 0) return dueDiff;
      return a.id - b.id;
    })
    .forEach((task) => {
      const applicationId = task.applicationId;
      if (applicationId == null) return;
      const key = `application-${applicationId}`;

      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          applicationId,
          applicationStatus: task.applicationStatus ?? null,
          applicationNumber: task.applicationNumber ?? null,
          applicationExternalNumber: task.applicationExternalNumber ?? null,
          applicationDate: task.applicationDate ?? null,
          applicationCreatedAt: task.applicationCreatedAt ?? null,
          applicationDirection: task.applicationDirection ?? null,
          clientName: task.applicationClientName ?? null,
          tasks: [],
          hasOverdue: false,
          nearestDueDate: null
        });
      }

      const group = grouped.get(key)!;
      group.tasks.push(task);
      group.hasOverdue = group.hasOverdue || isOverdueTask(task);

      const taskDue = task.dueDate ? dayjs(task.dueDate) : null;
      const currentDue = group.nearestDueDate ? dayjs(group.nearestDueDate) : null;
      if (taskDue?.isValid() && (!currentDue || !currentDue.isValid() || taskDue.isBefore(currentDue))) {
        group.nearestDueDate = task.dueDate ?? null;
      }
    });

  return Array.from(grouped.values()).sort((a, b) => {
    const aDue = a.nearestDueDate ? dayjs(a.nearestDueDate).valueOf() : Number.POSITIVE_INFINITY;
    const bDue = b.nearestDueDate ? dayjs(b.nearestDueDate).valueOf() : Number.POSITIVE_INFINITY;
    if (aDue !== bDue) return aDue - bDue;
    return a.applicationId - b.applicationId;
  });
}

interface BaseTaskListProps {
  tasks: WorkspaceTask[];
  /** Workspace applications for scoped group client labels (Phase 1D-C). */
  applications?: WorkspaceApplication[];
  /** Optional scoped presentation overrides (e.g. route drawer group→client map). */
  presentationContext?: ScopedTaskPresentationContext;
  /** Даты/поля заявки с списка workspace (без новых запросов): basisDate для колонки «Заявка клиента». */
  applicationContextById?: Record<number, { basisDate?: string | null }>;
  actionQueueLimit?: number;
  onTaskAction: (task: WorkspaceTask) => void | Promise<void>;
  /** Тот же callback, что в реестрах «Все маршруты» / «Все заявки» → DashboardFinanceContextDrawer. */
  onOpenFinanceContext?: (p: { kind: 'route' | 'application'; id: number }) => void;
  activeActionTaskId?: number | null;
  maxHeight?: number;
  bodyPadding?: number;
  groupTasksPadding?: string;
  title?: string;
}

function normalizeWs(s: string | null | undefined): string {
  return String(s || '').trim().toLowerCase();
}

function normalizedStatus(status: string | null | undefined): string {
  return String(status || '').trim().toUpperCase();
}

function isCompletedStatus(status: string | null | undefined): boolean {
  const value = normalizedStatus(status);
  return value === 'COMPLETED' || value === 'DONE' || value === 'ЗАВЕРШЕН' || value === 'ЗАВЕРШЁН';
}

function projectionFromTaskGroup(status: string | null | undefined, taskCount: number): CompletionProjection {
  const physicalComplete = isCompletedStatus(status);
  const overallComplete = physicalComplete && taskCount <= 0;
  return {
    physicalComplete,
    operationalComplete: taskCount <= 0,
    financialComplete: true,
    workflowComplete: taskCount <= 0,
    overallComplete,
    unresolved: overallComplete ? [] : ['Есть незавершенные задачи'],
  };
}

export const WorkspaceRouteTasks: React.FC<BaseTaskListProps> = ({
  tasks,
  applications,
  presentationContext,
  onTaskAction,
  onOpenFinanceContext,
  activeActionTaskId,
  maxHeight,
  bodyPadding,
  groupTasksPadding,
  title,
}) => {
  const routeGroups = useMemo(
    () => groupTasksByRoute(dedupeTasks(tasks.filter((task) => String(task.entityType || '').toUpperCase() === 'ROUTE'))),
    [tasks]
  );

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<WorkspaceStatusFilter>('all');
  const [hasTasksFilter, setHasTasksFilter] = useState<WorkspaceHasTasksFilter>('all');
  const [sortKey, setSortKey] = useState<WorkspaceSortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expandedRouteGroups, setExpandedRouteGroups] = useState<Record<string, boolean>>({});

  React.useEffect(() => {
    const raw = localStorage.getItem('workspace_route_tasks_filters_v1');
    if (!raw) return;
    try {
      const saved = JSON.parse(raw) as Partial<{
        search: string;
        statusFilter: WorkspaceStatusFilter;
        hasTasksFilter: WorkspaceHasTasksFilter;
      }>;
      if (saved.search != null) setSearch(saved.search);
      if (saved.statusFilter) setStatusFilter(saved.statusFilter);
      if (saved.hasTasksFilter) setHasTasksFilter(saved.hasTasksFilter);
    } catch {
      // ignore malformed saved state
    }
  }, []);

  React.useEffect(() => {
    localStorage.setItem(
      'workspace_route_tasks_filters_v1',
      JSON.stringify({ search, statusFilter, hasTasksFilter })
    );
  }, [search, statusFilter, hasTasksFilter]);

  const filteredSortedGroups = useMemo(() => {
    const q = normalizeWs(search);
    let list = routeGroups.filter((group) => {
      const direction =
        group.loadingCity || group.unloadingCity
          ? `${group.loadingCity ?? '—'} → ${group.unloadingCity ?? '—'}`
          : '—';
      const routeLabel = `#${group.routeId}`;
      const dates = formatWorkspaceDayMonth(group.nearestDueDate);
      const agreementLine = formatWorkspaceCarrierAgreementLine(group.carrierAgreement);
      const searchBlob = [
        direction,
        routeLabel,
        group.carrierName,
        agreementLine,
        group.carrierAgreement?.number,
        dates,
        String(group.routeId),
      ]
        .map(normalizeWs)
        .join(' ');
      if (q && !searchBlob.includes(q)) return false;
      const { overdue } = workspaceHighlightForRoute(group.routeId, tasks);
      const projection = projectionFromTaskGroup(group.routeStatus, group.tasks.length);
      if (!matchesRouteStatusFilter(group.routeStatus || '', statusFilter, overdue, projection)) return false;
      if (hasTasksFilter === 'without' && group.tasks.length > 0) return false;
      if (hasTasksFilter === 'with' && group.tasks.length === 0) return false;
      return true;
    });

    const effectiveKey = sortKey ?? 'date';
    const effectiveDir = sortKey == null ? 'desc' : sortDir;

    list = [...list].sort((a, b) => {
      if (effectiveKey === 'date') {
        const aDate = a.nearestDueDate ? dayjs(a.nearestDueDate).valueOf() : 0;
        const bDate = b.nearestDueDate ? dayjs(b.nearestDueDate).valueOf() : 0;
        const d = aDate - bDate;
        return effectiveDir === 'asc' ? d : -d;
      }
      if (effectiveKey === 'tasks') {
        const d = a.tasks.length - b.tasks.length;
        return effectiveDir === 'asc' ? d : -d;
      }
      const c = String(a.routeStatus || '').localeCompare(String(b.routeStatus || ''), 'ru');
      return effectiveDir === 'asc' ? c : -c;
    });

    return list;
  }, [routeGroups, search, statusFilter, sortKey, sortDir, tasks, hasTasksFilter]);

  const onHeaderSort = (key: WorkspaceSortKey) => {
    const next = toggleSort(key, sortKey, sortDir);
    setSortKey(next.key);
    setSortDir(next.dir);
  };

  const activeFilters = useMemo(() => {
    const items: string[] = [];
    if (search.trim()) items.push('Поиск');
    if (statusFilter === 'overdue') items.push('Просрочки');
    else if (statusFilter === 'in_work') items.push('В работе');
    else if (statusFilter === 'completed') items.push('Завершенные');
    if (hasTasksFilter === 'with') items.push('С задачами');
    if (hasTasksFilter === 'without') items.push('Без задач');
    return items;
  }, [search, statusFilter, hasTasksFilter]);

  const handleQuickFilter = (preset: 'problems' | 'overdue' | 'active' | 'without_tasks' | 'awaiting_docs') => {
    if (preset === 'problems' || preset === 'overdue') {
      setStatusFilter('overdue');
      setHasTasksFilter('with');
      return;
    }
    if (preset === 'active') {
      setStatusFilter('in_work');
      setHasTasksFilter('with');
      return;
    }
    if (preset === 'without_tasks') {
      setHasTasksFilter('without');
      setStatusFilter('all');
      return;
    }
    setSearch('документ');
    setHasTasksFilter('with');
  };

  const resetFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setHasTasksFilter('all');
  };

  const renderTask = (item: WorkspaceTask) => (
    <TaskCard
      key={item.id}
      workspaceTask={item}
      presentationContext={{ applications, ...presentationContext }}
      id={item.id}
      taskType={item.taskType}
      status={item.status}
      compact
      dueDate={item.dueDate}
      routeId={item.routeId}
      applicationId={item.applicationId}
      entityType={item.entityType}
      invoiceId={item.invoiceId}
      obligationId={item.obligationId}
      obligationStage={item.obligationStage}
      routeStatus={item.routeStatus}
      loadingCity={item.loadingCity}
      unloadingCity={item.unloadingCity}
      carrierName={item.carrierName}
      onAction={onTaskAction}
      actionLoading={activeActionTaskId === item.id}
    />
  );

  const renderGroup = (group: RouteTaskGroup) => {
    const direction =
      group.loadingCity || group.unloadingCity
        ? `${group.loadingCity ?? '—'} → ${group.unloadingCity ?? '—'}`
        : '—';
    const routeLabel = `#${group.routeId}`;
       const dates = formatWorkspaceDayMonth(group.nearestDueDate);
    const n = group.tasks.length;
    const agreementLine = formatWorkspaceCarrierAgreementLine(group.carrierAgreement);
    const highlight = workspaceHighlightForRoute(group.routeId, tasks);
    const rowStyle = workspaceRowHighlightStyle(highlight.overdue, highlight.today);
    const expanded = !!expandedRouteGroups[group.key];
    const overdueCount = group.tasks.filter((task) => isOverdueTask(task)).length;
    const summary =
      overdueCount > 0
        ? `${formatWorkspaceTasksCount(n)} • ${overdueCount} просрочена`
        : `${formatWorkspaceTasksCount(n)} • без просрочек`;

    return (
      <div
        key={group.key}
        style={{
          marginBottom: WORKSPACE_TASK_GROUP_MARGIN_BOTTOM_PX,
          ...rowStyle,
          borderRadius: 8,
          overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
        }}
      >
        <div
          role={onOpenFinanceContext ? 'button' : undefined}
          tabIndex={onOpenFinanceContext ? 0 : undefined}
          onClick={
            onOpenFinanceContext
              ? () => onOpenFinanceContext({ kind: 'route', id: group.routeId })
              : undefined
          }
          onKeyDown={
            onOpenFinanceContext
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpenFinanceContext({ kind: 'route', id: group.routeId });
                  }
                }
              : undefined
          }
          style={{
            ...WORKSPACE_ROUTE_GRID_ROW,
            borderBottom: '1px solid #f0f0f0',
            background: 'transparent',
            cursor: onOpenFinanceContext ? 'pointer' : undefined,
          }}
        >
          <Text strong style={WORKSPACE_GRID_CELL} title={direction}>
            {direction}
          </Text>
          <Text style={WORKSPACE_GRID_CELL}>{routeLabel}</Text>
          <Text type="secondary" style={WORKSPACE_GRID_CELL}>
            {dates}
          </Text>
          <Text type="secondary" style={WORKSPACE_GRID_CELL} title={group.carrierName ?? undefined}>
            {group.carrierName ?? '—'}
          </Text>
          <Text type="secondary" style={WORKSPACE_GRID_CELL} title={agreementLine}>
            {agreementLine}
          </Text>
          <Text style={WORKSPACE_GRID_CELL}>{summary}</Text>
          <span style={{ ...WORKSPACE_GRID_CELL, display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end' }}>
            <StatusCell status={group.routeStatus} />
            {highlight.overdue ? <Tag color="red">Просрочка</Tag> : null}
            <Button
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                setExpandedRouteGroups((prev) => ({ ...prev, [group.key]: !expanded }));
              }}
            >
              {expanded ? 'Скрыть' : 'Показать'}
            </Button>
          </span>
        </div>
        {expanded && (
          <div style={{ padding: groupTasksPadding ?? WORKSPACE_TASK_GROUP_EXPANDED_PADDING }}>{group.tasks.map((task) => renderTask(task))}</div>
        )}
      </div>
    );
  };

  const emptyNoRoutes = !routeGroups.length;
  const emptyAfterFilter = !emptyNoRoutes && !filteredSortedGroups.length;

  return (
    <Card
      size="small"
      title={title ?? 'Маршруты с задачами'}
      styles={{
        body: bodyPadding != null ? { padding: bodyPadding } : WORKSPACE_TASK_CARD_BODY_PADDING,
      }}
    >
      <WorkspaceListToolbar
        searchPlaceholder="Номер маршрута, перевозчик, направление…"
        search={search}
        onSearch={setSearch}
        status={statusFilter}
        onStatus={setStatusFilter}
        scope="all"
        onScope={() => {}}
        hasTasks={hasTasksFilter}
        onHasTasks={setHasTasksFilter}
        showScopeFilter={false}
        onQuickFilter={handleQuickFilter}
        activeFilters={activeFilters}
        onResetFilters={resetFilters}
      />
      <div style={{ ...WORKSPACE_TASK_LIST_SCROLL_STYLE, ...(maxHeight != null ? { minHeight: maxHeight, maxHeight } : null) }}>
        <div style={WORKSPACE_ROUTE_TASK_STICKY_HEADER}>
          <WorkspaceHeaderCell sortKey={undefined} activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Направление
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey={undefined} activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            №
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey="date" activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Дата
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey={undefined} activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Перевозчик
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey={undefined} activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Договор
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey="tasks" activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Задачи
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey="status" activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Статус
          </WorkspaceHeaderCell>
        </div>
        {emptyNoRoutes ? (
          <Empty description="Нет активных задач по маршрутам" style={{ marginTop: 48 }} />
        ) : emptyAfterFilter ? (
          <Empty description="Нет строк по фильтрам" style={{ margin: '24px 0' }} />
        ) : (
          filteredSortedGroups.map((group) => renderGroup(group))
        )}
      </div>
    </Card>
  );
};

export const WorkspaceApplicationTasks: React.FC<BaseTaskListProps> = ({
  tasks,
  applications,
  presentationContext,
  applicationContextById,
  onTaskAction,
  onOpenFinanceContext,
  activeActionTaskId,
  maxHeight,
  bodyPadding,
  groupTasksPadding,
  title,
}) => {
  const applicationGroups = useMemo(
    () =>
      groupTasksByApplication(
        dedupeTasks(tasks.filter((task) => String(task.entityType || '').toUpperCase() === 'APPLICATION'))
      ),
    [tasks]
  );

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<WorkspaceStatusFilter>('all');
  const [hasTasksFilter, setHasTasksFilter] = useState<WorkspaceHasTasksFilter>('all');
  const [sortKey, setSortKey] = useState<WorkspaceSortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expandedApplicationGroups, setExpandedApplicationGroups] = useState<Record<string, boolean>>({});

  React.useEffect(() => {
    const raw = localStorage.getItem('workspace_application_tasks_filters_v1');
    if (!raw) return;
    try {
      const saved = JSON.parse(raw) as Partial<{
        search: string;
        statusFilter: WorkspaceStatusFilter;
        hasTasksFilter: WorkspaceHasTasksFilter;
      }>;
      if (saved.search != null) setSearch(saved.search);
      if (saved.statusFilter) setStatusFilter(saved.statusFilter);
      if (saved.hasTasksFilter) setHasTasksFilter(saved.hasTasksFilter);
    } catch {
      // ignore malformed saved state
    }
  }, []);

  React.useEffect(() => {
    localStorage.setItem(
      'workspace_application_tasks_filters_v1',
      JSON.stringify({ search, statusFilter, hasTasksFilter })
    );
  }, [search, statusFilter, hasTasksFilter]);

  const filteredSortedGroups = useMemo(() => {
    const q = normalizeWs(search);
    let list = applicationGroups.filter((group) => {
      const direction = group.applicationDirection || '—';
      const internalLines = workspaceApplicationNumberLines(group.applicationNumber, group.applicationDate);
      const basis = applicationContextById?.[group.applicationId]?.basisDate;
      const clientLines = workspaceClientApplicationLines(
        group.applicationExternalNumber,
        basis ?? group.applicationDate
      );
      const dateCol =
        group.applicationCreatedAt != null && String(group.applicationCreatedAt).trim() !== ''
          ? formatWorkspaceDayMonth(group.applicationCreatedAt)
          : formatWorkspaceDayMonth(group.applicationDate);
      const searchBlob = [
        direction,
        internalLines.line1,
        internalLines.line2,
        clientLines.line1,
        clientLines.line2,
        String(group.applicationId),
        group.clientName,
        dateCol,
      ]
        .map(normalizeWs)
        .join(' ');
      if (q && !searchBlob.includes(q)) return false;
      const { overdue } = workspaceHighlightForApplication(group.applicationId, tasks);
      const projection = projectionFromTaskGroup(group.applicationStatus, group.tasks.length);
      if (!matchesApplicationStatusFilter(group.applicationStatus || '', statusFilter, overdue, projection)) return false;
      if (hasTasksFilter === 'without' && group.tasks.length > 0) return false;
      if (hasTasksFilter === 'with' && group.tasks.length === 0) return false;
      return true;
    });

    const effectiveKey = sortKey ?? 'date';
    const effectiveDir = sortKey == null ? 'desc' : sortDir;

    list = [...list].sort((a, b) => {
      if (effectiveKey === 'date') {
        const aDate =
          a.applicationCreatedAt != null && String(a.applicationCreatedAt).trim() !== ''
            ? dayjs(a.applicationCreatedAt).valueOf()
            : a.applicationDate
              ? dayjs(a.applicationDate).valueOf()
              : 0;
        const bDate =
          b.applicationCreatedAt != null && String(b.applicationCreatedAt).trim() !== ''
            ? dayjs(b.applicationCreatedAt).valueOf()
            : b.applicationDate
              ? dayjs(b.applicationDate).valueOf()
              : 0;
        const d = aDate - bDate;
        return effectiveDir === 'asc' ? d : -d;
      }
      if (effectiveKey === 'tasks') {
        const d = a.tasks.length - b.tasks.length;
        return effectiveDir === 'asc' ? d : -d;
      }
      const c = String(a.applicationStatus || '').localeCompare(String(b.applicationStatus || ''), 'ru');
      return effectiveDir === 'asc' ? c : -c;
    });

    return list;
  }, [applicationGroups, search, statusFilter, sortKey, sortDir, tasks, hasTasksFilter, applicationContextById]);

  const onHeaderSort = (key: WorkspaceSortKey) => {
    const next = toggleSort(key, sortKey, sortDir);
    setSortKey(next.key);
    setSortDir(next.dir);
  };

  const activeFilters = useMemo(() => {
    const items: string[] = [];
    if (search.trim()) items.push('Поиск');
    if (statusFilter === 'overdue') items.push('Просрочки');
    else if (statusFilter === 'in_work') items.push('В работе');
    else if (statusFilter === 'completed') items.push('Завершенные');
    if (hasTasksFilter === 'with') items.push('С задачами');
    if (hasTasksFilter === 'without') items.push('Без задач');
    return items;
  }, [search, statusFilter, hasTasksFilter]);

  const handleQuickFilter = (preset: 'problems' | 'overdue' | 'active' | 'without_tasks' | 'awaiting_docs') => {
    if (preset === 'problems' || preset === 'overdue') {
      setStatusFilter('overdue');
      setHasTasksFilter('with');
      return;
    }
    if (preset === 'active') {
      setStatusFilter('in_work');
      setHasTasksFilter('with');
      return;
    }
    if (preset === 'without_tasks') {
      setHasTasksFilter('without');
      setStatusFilter('all');
      return;
    }
    setSearch('документ');
    setHasTasksFilter('with');
  };

  const resetFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setHasTasksFilter('all');
  };

  const renderTask = (item: WorkspaceTask) => (
    <TaskCard
      key={item.id}
      workspaceTask={item}
      presentationContext={{ applications, ...presentationContext }}
      id={item.id}
      taskType={item.taskType}
      status={item.status}
      compact
      dueDate={item.dueDate}
      routeId={item.routeId}
      applicationId={item.applicationId}
      entityType={item.entityType}
      invoiceId={item.invoiceId}
      obligationId={item.obligationId}
      obligationStage={item.obligationStage}
      routeStatus={item.routeStatus}
      loadingCity={item.loadingCity}
      unloadingCity={item.unloadingCity}
      carrierName={item.carrierName}
      onAction={onTaskAction}
      actionLoading={activeActionTaskId === item.id}
    />
  );

  const emptyNoApps = !applicationGroups.length;
  const emptyAfterFilter = !emptyNoApps && !filteredSortedGroups.length;

  return (
    <Card
      size="small"
      title={title ?? 'Заявки с задачами'}
      styles={{
        body: bodyPadding != null ? { padding: bodyPadding } : WORKSPACE_TASK_CARD_BODY_PADDING,
      }}
    >
      <WorkspaceListToolbar
        searchPlaceholder="Внешний №, клиент, направление…"
        search={search}
        onSearch={setSearch}
        status={statusFilter}
        onStatus={setStatusFilter}
        scope="all"
        onScope={() => {}}
        hasTasks={hasTasksFilter}
        onHasTasks={setHasTasksFilter}
        showScopeFilter={false}
        onQuickFilter={handleQuickFilter}
        activeFilters={activeFilters}
        onResetFilters={resetFilters}
      />
      <div style={{ ...WORKSPACE_TASK_LIST_SCROLL_STYLE, ...(maxHeight != null ? { minHeight: maxHeight, maxHeight } : null) }}>
        <div style={WORKSPACE_APPLICATION_TASKS_STICKY_HEADER}>
          <WorkspaceHeaderCell sortKey="date" activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            № заявки
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey={undefined} activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Направление
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey={undefined} activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Клиент
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey={undefined} activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Заявка клиента
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey="tasks" activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Задачи
          </WorkspaceHeaderCell>
          <WorkspaceHeaderCell sortKey="status" activeKey={sortKey} activeDir={sortDir} onSort={onHeaderSort}>
            Статус
          </WorkspaceHeaderCell>
        </div>
        {emptyNoApps ? (
          <Empty description="Нет активных задач по заявкам" style={{ marginTop: 48 }} />
        ) : emptyAfterFilter ? (
          <Empty description="Нет строк по фильтрам" style={{ margin: '24px 0' }} />
        ) : (
          filteredSortedGroups.map((group) => {
            const direction = group.applicationDirection || '—';
            const internalLines = workspaceApplicationNumberLines(group.applicationNumber, group.applicationDate);
            const basis = applicationContextById?.[group.applicationId]?.basisDate;
            const clientLines = workspaceClientApplicationLines(
              group.applicationExternalNumber,
              basis ?? group.applicationDate
            );
            const n = group.tasks.length;
            const highlight = workspaceHighlightForApplication(group.applicationId, tasks);
            const rowStyle = workspaceRowHighlightStyle(highlight.overdue, highlight.today);
            const expanded = !!expandedApplicationGroups[group.key];
            const overdueCount = group.tasks.filter((task) => isOverdueTask(task)).length;
            const summary =
              overdueCount > 0
                ? `${formatWorkspaceTasksCount(n)} • ${overdueCount} просрочена`
                : `${formatWorkspaceTasksCount(n)} • без просрочек`;
            const cellWrap: React.CSSProperties = {
              ...WORKSPACE_GRID_CELL,
              whiteSpace: 'normal',
              lineHeight: 1.3,
            };

            return (
              <div
                key={group.key}
                style={{
                  marginBottom: WORKSPACE_TASK_GROUP_MARGIN_BOTTOM_PX,
                  ...rowStyle,
                  borderRadius: 8,
                  overflow: 'hidden',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
                }}
              >
                <div
                  role={onOpenFinanceContext ? 'button' : undefined}
                  tabIndex={onOpenFinanceContext ? 0 : undefined}
                  onClick={
                    onOpenFinanceContext
                      ? () => onOpenFinanceContext({ kind: 'application', id: group.applicationId })
                      : undefined
                  }
                  onKeyDown={
                    onOpenFinanceContext
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onOpenFinanceContext({ kind: 'application', id: group.applicationId });
                          }
                        }
                      : undefined
                  }
                  style={{
                    ...WORKSPACE_APPLICATION_GRID_ROW,
                    borderBottom: '1px solid #f0f0f0',
                    background: 'transparent',
                    cursor: onOpenFinanceContext ? 'pointer' : undefined,
                  }}
                >
                  <div style={cellWrap}>
                    <WorkspaceTwoLineTableCell line1={internalLines.line1} line2={internalLines.line2} />
                  </div>
                  <Text strong style={cellWrap} title={direction}>
                    {direction}
                  </Text>
                  <Text type="secondary" style={cellWrap} title={group.clientName ?? undefined}>
                    {group.clientName ?? '—'}
                  </Text>
                  <div style={cellWrap}>
                    <WorkspaceTwoLineTableCell line1={clientLines.line1} line2={clientLines.line2} />
                  </div>
                  <Text style={WORKSPACE_GRID_CELL}>{summary}</Text>
                  <span style={{ ...WORKSPACE_GRID_CELL, display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end' }}>
                    <StatusCell status={group.applicationStatus} />
                    {highlight.overdue ? <Tag color="red">Просрочка</Tag> : null}
                    <Button
                      size="small"
                      type="text"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedApplicationGroups((prev) => ({ ...prev, [group.key]: !expanded }));
                      }}
                    >
                      {expanded ? 'Скрыть' : 'Показать'}
                    </Button>
                  </span>
                </div>
                {expanded && (
                  <div style={{ padding: groupTasksPadding ?? WORKSPACE_TASK_GROUP_EXPANDED_PADDING }}>
                    {group.tasks.map((task) => renderTask(task))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
};
